import type { OrchestratorSchemas } from '@ctcells/contract'
import { REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { keys } from '../keys.ts'

/**
 * 서비스 종착 모듈 행 지우기 — `DELETE /modules/{id}`(`IFC-RO-108` · operation `deleteModule` · v0.9.4).
 *
 * 실물(`ctcells-core/app/views/modules.py` `delete_module` · dev2 openapi 2026-10-11)
 *
 * | 칸 | 값 |
 * | --- | --- |
 * | 본문 | `IrreversibleCommandEnvelope{command_id, reason(필수 · 빈 문자열 422)}` |
 * | 헤더 | 🔴 **`X-Control-Token` 없음** — 서버가 `require_control_lock` 을 걸지 않는다(openapi parameters 도 `id` 하나) |
 * | 역할 | **admin 만**(`ENDPOINT_ROLES` · `/auth/me` `deleteModule: {admin}`) |
 * | 200 | `ModuleDeleteResult{module_id, deleted: {표 이름: 지운 행 수}}` — 0 인 표도 실린다 |
 * | 404 | 모듈이 없다(표준) |
 * | 409 | `MODULE_DELETE_REJECTED` · `context.reason` ∈ `not_terminated` · `batch_open`(+`batch_id`) · `execution_open` — **아무것도 안 지웠다** |
 *
 * 🔴 **비가역이다.** 모듈 행과 FK 자식 여덟이 같은 트랜잭션에서 지워진다. 배치 기록 · 감사 · 시계열의 `module_id` 는 남는다(고아 참조).
 * 🔴 **재시도에 `commandId` 를 새로 만들지 마라** — 같은 값이면 서버가 첫 답을 그대로 준다(`IFC-COM-03`).
 */
export type ModuleDeleteResult = OrchestratorSchemas['schemas']['ModuleDeleteResult']

/** `MODULE_DELETE_REJECTED` 의 `context.reason` 어휘 — 서버 `views/modules.py` `DELETE_*` 셋 그대로 */
export const MODULE_DELETE_REASONS = ['not_terminated', 'batch_open', 'execution_open'] as const
export type ModuleDeleteReason = (typeof MODULE_DELETE_REASONS)[number]

export interface DeleteModuleVars {
  moduleId: string
  /** 🔴 감사 원장에 봉인된다 — 사람이 적는다. 비우지 않는다 */
  reason: string
  commandId?: string
}

export const useDeleteModule = createCommand<DeleteModuleVars, ModuleDeleteResult>({
  send: ({ moduleId, reason, commandId }) =>
    orchestrator.request<ModuleDeleteResult>({
      method: 'DELETE',
      path: `/modules/${encodeURIComponent(moduleId)}`,
      body: { command_id: commandId ?? newCommandId(), reason },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  // 모듈 목록 · 상세를 서버에 다시 묻는다(낙관적 갱신 금지 · DP-04)
  invalidates: () => [keys.modules.all()],
})
